/**
 * Persona derivation from hostname.
 *
 * Each subdomain of tasteoftulum.org renders a different persona experience
 * from the SAME React bundle. The user-perceived effect is three distinct
 * apps that install as separate PWAs (separate icons, separate names, separate
 * service-worker scopes — because each origin is its own SW scope).
 *
 * Mapping at deploy time (Cloudflare Pages custom domains, single project):
 *   - app.tasteoftulum.org       → attendee  (consumer / ticket holder)
 *   - partners.tasteoftulum.org  → partner   (restaurant / hotel / artist / nonprofit backoffice)
 *   - admin.tasteoftulum.org     → organizer (festival admin)
 *
 * The root `tasteoftulum.org` is NOT served by this app — that's the legacy
 * static marketing site (flyer + partner-signup form) which still ships from
 * the repo root via the original Pages project.
 *
 * Local dev (Vite on localhost:5173) defaults to `attendee`. Override with a
 * `?persona=` query param or by editing `LOCAL_DEV_PERSONA` below.
 */

import type { Role } from "@/lib/auth";

export interface PersonaBrand {
  /** Human-readable name shown in chrome + PWA install dialog. */
  name: string;
  /** Short name (≤ 12 chars) for the PWA home-screen icon. */
  shortName: string;
  /** One-line tagline under the hero on the landing page. */
  tagline: string;
  /** Path under `/icons/` for the PWA install icon (192 + 512 variants assumed). */
  iconBase: string;
  /** Theme color for the PWA / address bar. */
  themeColor: string;
  /** Header brand lockup — "Eat With Me presents Taste of Tulum by Art With Me". */
  logoSrc: {
    presents: string;
    mark: string;
    by: string;
  };
}

/** Shared festival brand lockup — same 3 logos across every persona today. */
const BRAND_LOGO = {
  presents: "/assets/eatwithme-white.png",
  mark: "/assets/tulum-logo-gold.png",
  by: "/assets/awm-white.png",
};

export interface Persona {
  /** Which Cognito group is required to access /app/*. */
  role: Role;
  /** Brand surface — fed into landing, manifest, header. */
  brand: PersonaBrand;
}

/* ---------------------------------------------------------------- */
/* Persona definitions                                              */
/* ---------------------------------------------------------------- */

export const PERSONAS: Record<Role, Persona> = {
  attendee: {
    role: "attendee",
    brand: {
      name: "Taste of Tulum",
      shortName: "Taste of Tulum",
      tagline: "Browse events. Buy tickets. Carry them in your pocket — even offline.",
      iconBase: "/icons/attendee",
      themeColor: "#11302a",
      logoSrc: BRAND_LOGO,
    },
  },
  partner: {
    role: "partner",
    brand: {
      name: "Taste of Tulum Partners",
      shortName: "ToT Partners",
      tagline: "Your backoffice for the festival. Profile, offerings, orders, payouts.",
      iconBase: "/icons/partners",
      themeColor: "#11302a",
      logoSrc: BRAND_LOGO,
    },
  },
  organizer: {
    role: "organizer",
    brand: {
      name: "Taste of Tulum Organizer",
      shortName: "ToT Organizer",
      tagline: "Festival control plane. Tenants, schedule, finance, audit.",
      iconBase: "/icons/organizer",
      themeColor: "#11302a",
      logoSrc: BRAND_LOGO,
    },
  },
};

/* ---------------------------------------------------------------- */
/* Hostname → persona resolution                                    */
/* ---------------------------------------------------------------- */

const HOSTNAME_MAP: Record<string, Role> = {
  "app.tasteoftulum.org": "attendee",
  "partners.tasteoftulum.org": "partner",
  "admin.tasteoftulum.org": "organizer",
  // Add additional environments here (e.g. staging.app.tasteoftulum.org)
};

/** Local dev default — flip while building a specific persona. */
const LOCAL_DEV_PERSONA: Role = "attendee";

/**
 * Resolve the current persona from `window.location.hostname` plus query.
 *
 * Resolution order:
 *   1. URL `?persona=attendee|partner|organizer` (dev override).
 *   2. `localStorage.persona` (sticky dev override).
 *   3. HOSTNAME_MAP exact match.
 *   4. Fallback to LOCAL_DEV_PERSONA.
 *
 * Always returns a valid Persona — never undefined — so callers don't need
 * to thread null checks through every component.
 */
export function resolvePersona(): Persona {
  if (typeof window === "undefined") return PERSONAS[LOCAL_DEV_PERSONA];

  // 1. URL override (most explicit)
  const url = new URL(window.location.href);
  const q = url.searchParams.get("persona");
  if (q && isRole(q)) {
    // Persist so reloads inside the dev session keep the override.
    try { localStorage.setItem("persona", q); } catch { /* private mode */ }
    return PERSONAS[q];
  }

  // 2. Sticky local override
  try {
    const stored = localStorage.getItem("persona");
    if (stored && isRole(stored)) return PERSONAS[stored];
  } catch { /* private mode */ }

  // 3. Hostname map
  const host = window.location.hostname.toLowerCase();
  const mapped = HOSTNAME_MAP[host];
  if (mapped) return PERSONAS[mapped];

  // 4. Fallback (localhost, *.pages.dev preview, anything not mapped)
  return PERSONAS[LOCAL_DEV_PERSONA];
}

function isRole(s: string): s is Role {
  return s === "attendee" || s === "partner" || s === "organizer";
}
